import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '../services/auth.service';
import { from, switchMap, catchError, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { estDestinationApi } from '@stockflow/acces/angular';
import { environment } from '../../../environments/environment';

/**
 * SF-06 — le jeton et les en-têtes d'identité ne partent que vers une API
 * déclarée dans l'environnement, ou vers l'application elle-même.
 */
const BASES_API: readonly string[] = Object.values(environment.apiUrls);

// Track whether a logout is already in progress to prevent cascading logouts
let logoutInProgress = false;
// Track whether sync-push is in flight — don't logout during sync
let syncInProgress = false;
// Grace period after app startup to avoid 401 → logout cascade during initialization
const appStartTime = Date.now();
const STARTUP_GRACE_MS = 5000;

/** Called by SyncPushService to protect against 401→logout during sync */
export function setSyncInProgress(v: boolean): void { syncInProgress = v; }

/** API paths that should not trigger a 401→logout redirect (public or semi-public) */
const PUBLIC_API_PATHS = [
    '/plans/self-service',
    '/plans/public',
    '/auth/register',
    '/auth/verify-email',
    '/auth/resend-code',
    '/ref-data/jurisdictions',
    '/ref-data/jurisdiction/',
    '/ref-data/legal-forms',
    '/ref-data/fiscal-identifiers',
    '/ref-data/fiscal-regimes',
    '/ref-data/payroll-bodies',
    '/ref-data/accounting-standards',
    '/ref-data/currencies',
    '/ref-data/countries',
    '/ref-data/activity-sectors',
    '/ref-data/languages',
    '/reference-data/templates',
];

export const authInterceptor: HttpInterceptorFn = (req, next) => {
    // Skip auth for local assets (i18n, images, etc.)
    if (req.url.startsWith('./assets/') || req.url.startsWith('/assets/') || req.url.startsWith('assets/')) {
        return next(req);
    }
    // SF-06 : aucun jeton vers un tiers (S3, visioconférence, CDN, URL reçue).
    if (!estDestinationApi(req.url, BASES_API, globalThis.document?.baseURI ?? globalThis.location?.href)) {
        return next(req);
    }

    const authService = inject(AuthService);
    const router = inject(Router);

    return from(authService.getToken()).pipe(
        switchMap(token => {
            const isS3Url = req.url.includes('.s3.') || req.url.includes('s3.amazonaws.com');

            if (token && !isS3Url) {
                // Chain retrieval of companyId
                return from(Promise.all([authService.getCompanyId(), authService.getUserName()])).pipe(
                    switchMap(([companyId, userName]) => {
                        const headers: any = {
                            Authorization: `Bearer ${token}`
                        };
                        if (companyId) {
                            headers['x-company-id'] = companyId;
                        }
                        if (userName) {
                            headers['x-user-name'] = encodeURIComponent(userName);
                        }
                        // Send email separately — x-user-name may be a display name
                        const userEmail = localStorage.getItem('currentUserEmail');
                        if (userEmail) {
                            headers['x-user-email'] = encodeURIComponent(userEmail);
                        }
                        const warehouseId = localStorage.getItem('selectedWarehouseId');
                        if (warehouseId) {
                            headers['x-warehouse-id'] = warehouseId;
                        }
                        const clonedReq = req.clone({ setHeaders: headers });
                        return next(clonedReq);
                    })
                );
            }
            return next(req);
        }),
        catchError((error: HttpErrorResponse) => {
            if (error.status === 401) {
                const currentUrl = router.url;
                const isStartup = (Date.now() - appStartTime) < STARTUP_GRACE_MS;
                const isPublicPath = PUBLIC_API_PATHS.some(p => req.url.includes(p));
                // Don't trigger logout if: on login page, logout in progress, startup grace, offline, or public endpoint
                if (currentUrl.includes('/login') || currentUrl.includes('/onboarding') || logoutInProgress || isStartup || !navigator.onLine || isPublicPath || syncInProgress) {
                    console.warn('[AuthInterceptor] 401 ignored (login/startup/logout-in-progress/offline):', req.url);
                } else {
                    console.error('[AuthInterceptor] Session expired — 401 from:', req.url);
                    logoutInProgress = true;
                    authService.logout().finally(() => { logoutInProgress = false; });
                }
            }
            return throwError(() => error);
        })
    );
};
